import { useId } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import type { NavSection } from "../navigation/navigation";
import { SidebarNavItem } from "./SidebarNavItem";

export interface SidebarNavGroupProps {
  section: NavSection;
  collapsed?: boolean;
  onNavigate?: () => void;
}

/**
 * Un grupo de la navegación, con su rotulillo.
 *
 * ── El rotulillo no es una píldora ───────────────────────────────────
 * Texto plano en mayúsculas con tracking ancho. Anahí retiró del sistema
 * las cápsulas de rótulo (fondo lila + texto violeta): el espaciado en
 * mayúsculas ya crea la jerarquía, la cápsula solo añade ruido.
 *
 * ── Plegado ──────────────────────────────────────────────────────────
 * El rotulillo se vuelve `sr-only`, NO desaparece: en el riel no hay
 * sitio para escribirlo, pero quien navega con lector de pantalla sigue
 * necesitando saber dónde empieza cada grupo. Lo que se ve entonces es un
 * filete que separa los grupos.
 *
 * ── `external` ───────────────────────────────────────────────────────
 * Las pantallas que viven fuera del armazón se separan con un filete y se
 * pintan más apagadas, y cada enlace lleva la flecha de "esto sale de
 * aquí". Así se nota de un vistazo que la tienda y el acceso no son
 * secciones del panel.
 */
export function SidebarNavGroup({
  section,
  collapsed = false,
  onNavigate,
}: SidebarNavGroupProps) {
  const { t } = useTranslation();
  const labelId = useId();

  return (
    <div
      className={cn(
        "pt-4 first:pt-0",
        section.external && "mt-1 border-t border-ds-border pt-4",
      )}
    >
      <p
        id={labelId}
        className={cn(
          // `leading-none` recortaba la tilde de "ADMINISTRACIÓN": con
          // line-height igual al tamaño de letra, el acento de una mayúscula
          // queda justo en el borde de la caja y basta un ancestro que
          // recorte para comérselo. 1.4 le deja sitio.
          "text-[10px] font-semibold uppercase leading-[1.4] tracking-ds-label-wide text-ds-ink-3",
          collapsed ? "sr-only" : "px-3 pb-2",
        )}
      >
        {t(section.label)}
      </p>

      {!collapsed && section.note && (
        <p className="px-3 pb-2 text-[11px] leading-snug text-ds-ink-3">{t(section.note)}</p>
      )}

      <ul aria-labelledby={labelId} className={cn("grid gap-1", collapsed && "justify-items-center")}>
        {section.items.map((item) => (
          <SidebarNavItem
            key={item.to}
            item={item}
            collapsed={collapsed}
            external={section.external}
            onNavigate={onNavigate}
          />
        ))}
      </ul>
    </div>
  );
}
